import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { Button } from './Button';
import { useFocusTrap, useScrollLock } from './Modal';

export type DrawerSide = 'right' | 'left';
export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl';

const WIDTHS: Record<DrawerWidth, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  // The dispatch bay works one vehicle at a time and wants room for the form
  // beside its photos (10 Sep 2026).
  xl: 'sm:max-w-4xl',
};

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  side?: DrawerSide;
  width?: DrawerWidth;
  children: ReactNode;
  footer?: ReactNode;
  /** Right-aligned controls in the header, beside the close button. */
  headerActions?: ReactNode;
  className?: string;
  /**
   * A click on the shade beside the panel closes it — except on a long form,
   * where that click is usually someone coming back to the window (22 Oct
   * 2026: "the form closes if the tabs are switched"). Close then means the
   * close button or Escape.
   */
  closeOnBackdrop?: boolean;
}

/**
 * A side panel for filters, a record preview, or the navigation on a small screen.
 *
 * Full width below the `sm` breakpoint, because a 300px panel on a 375px phone is
 * neither a drawer nor a page.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  side = 'right',
  width = 'md',
  children,
  footer,
  headerActions,
  className,
  closeOnBackdrop = true,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useScrollLock(open);
  useFocusTrap(open, panelRef, onClose);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-x-0 z-100 flex print-hide"
      /*
       * The VISIBLE viewport, not the layout one: with the keyboard up these
       * differ by half the screen, and a footer pinned to the layout viewport
       * is a submit button the counter cannot reach (owner, 16 Sep 2026).
       */
      style={{ top: 'var(--app-vtop, 0px)', height: 'var(--app-vh, 100dvh)' }}
    >
      <div
        aria-hidden
        onClick={closeOnBackdrop ? onClose : undefined}
        className="absolute inset-0 bg-[color-mix(in_oklab,var(--c-ink)_45%,transparent)] animate-fade-in"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={clsx(
          'relative z-10 ml-auto flex h-full w-full flex-col bg-surface shadow-float',
          WIDTHS[width],
          side === 'left'
            ? 'mr-auto ml-0 animate-slide-in-left border-r border-line'
            : 'animate-slide-in-right border-l border-line',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3.5 pt-[max(0.875rem,env(safe-area-inset-top))] sm:px-5 sm:pt-3.5">
          <div className="min-w-0">
            <h2 className="font-display text-body font-semibold text-ink">{title}</h2>
            {description ? <p className="mt-0.5 text-micro text-muted">{description}</p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {headerActions}
            <Button variant="ghost" size="sm" iconOnly onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface px-4 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] sm:px-5 sm:pb-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
